import React from "react";
import "./RunQueue.css";
import { queuedTileLabel, isHeldEntry } from "./runQueueFormat";

/**
 * Run-tile status for the queue's pre-execution states.
 *
 *   held            → "Queued · not started" (an admin starts it from Multi-project
 *                     runs) + Cancel — no Bump: it is not in the admission order
 *   queued          → "Queued · #3 · ~90 min" (waiting reason as tooltip) + Cancel
 *                     (+ Bump when the caller passes onBump — admins only)
 *   cancelled       → greyed "Cancelled" + Requeue
 *   dispatch_failed → "Dispatch failed" (error as tooltip) + Requeue
 *
 * `run` is the dashboard run (status from the task list); `entry` is the
 * matching /rsa/v2/queue row when known — position/ETA/reason come from it,
 * falling back to the metadata the backend seeded at enqueue time
 * (`queue_status: "held"` marks a held run before the queue view has loaded).
 */
export const HELD_TILE_TITLE =
  "Not started — an admin starts it from Multi-project runs";

export const queueStateForRun = (run, entry) => {
  const runStatus = String(run?.status || "").toLowerCase();
  const entryStatus = String(entry?.status || "").toLowerCase();
  if (entryStatus === "dispatch_failed") return "dispatch_failed";
  if (entryStatus === "cancelled" || runStatus === "cancelled") return "cancelled";
  if (entryStatus === "held") return "held";
  if (entryStatus === "queued") return "queued";
  if (runStatus === "queued") return isHeldEntry(null, run) && !entry ? "held" : "queued";
  return null;
};

function QueuedRunBadge({
  run,
  entry = null,
  onCancel = null,
  onBump = null,
  onRequeue = null,
  busy = false,
}) {
  const state = queueStateForRun(run, entry);
  if (!state) return null;

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const seeded = {
    position: run?.metadata?.queue_position ?? run?.queue?.position ?? null,
    eta_minutes: run?.queue?.eta_minutes ?? null,
    status: run?.metadata?.queue_status ?? run?.queue?.status ?? null,
  };
  const labelEntry = entry || seeded;
  const canAct = Boolean(entry?.id) && !busy;

  if (state === "held" || state === "queued") {
    const held = state === "held";
    const reason = held
      ? HELD_TILE_TITLE
      : entry?.waiting_reason ||
        (Number(labelEntry.position) > 1 ? "Waiting for the runs ahead of it" : "Waiting for GPU capacity");
    return (
      <span className="queued-run-badge" onClick={stop} data-testid="queued-run-badge">
        <span className={`project-status status-queued${held ? " status-queued--held" : ""}`} title={reason}>
          {held ? queuedTileLabel({ status: "held" }) : queuedTileLabel(labelEntry)}
        </span>
        {onCancel && (
          <button
            type="button"
            className="queued-run-action"
            disabled={!canAct}
            title={canAct ? "Cancel this queued run" : "Queue entry not loaded yet"}
            onClick={(e) => {
              stop(e);
              onCancel(entry);
            }}
          >
            Cancel
          </button>
        )}
        {onBump && !held && (
          <button
            type="button"
            className="queued-run-action queued-run-action--bump"
            disabled={!canAct}
            title={canAct ? "Move to the front of the queue" : "Queue entry not loaded yet"}
            onClick={(e) => {
              stop(e);
              onBump(entry);
            }}
          >
            Bump
          </button>
        )}
      </span>
    );
  }

  const label = state === "cancelled" ? "Cancelled" : "Dispatch failed";
  const title =
    state === "cancelled"
      ? "Cancelled before start — Requeue to put it back in line"
      : entry?.error || "The queue could not start this run";
  return (
    <span className="queued-run-badge" onClick={stop} data-testid="queued-run-badge">
      <span className={`project-status status-cancelled`} title={title}>
        {label}
      </span>
      {onRequeue && (
        <button
          type="button"
          className="queued-run-action queued-run-action--requeue"
          disabled={!canAct}
          title={canAct ? "Put this run back in the queue" : "Queue entry not loaded yet"}
          onClick={(e) => {
            stop(e);
            onRequeue(entry);
          }}
        >
          Requeue
        </button>
      )}
    </span>
  );
}

export default QueuedRunBadge;
